import React, { useState, useEffect } from 'react';
import { Plus, Truck, Clock, CheckCircle, Circle, Package, ChevronUp, User, Box } from 'lucide-react';
import { Route, Stop, Vehicle, Driver } from '../../types';
import { routeApi, stopApi, vehicleApi, driverApi, tripApi } from '../../services/api';
import { useAuthStore } from '../../stores/authStore';
import LoadingSpinner from '../ui/LoadingSpinner';
import { MapContainer, TileLayer, Marker, Popup, Polyline } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

interface TripStop {
  id: number;
  name: string;
  type: 'pending' | 'accepted' | 'pickup' | 'loading' | 'en_route' | 'delivery' | 'depot';
  address: string;
  latitude: number;
  longitude: number;
  sequence: number;
  estimated_time: string;
  status: 'pending' | 'active' | 'completed';
  actual_time?: string;
  // Event-based fields
  first_entry?: string;
  last_exit?: string;
  entry_count?: number;
  exit_count?: number;
  computed_status?: string;
  status_reason?: string;
}

interface Trip {
  id: number;
  job_number: string;
  vehicle_name: string;
  driver_name: string;
  vehicle_lat: number;
  vehicle_lng: number;
  cargo: string;
  status: string;
  stops: TripStop[];
}

// Helper function to get computed stop status based on events
const getStopStatus = (stop: TripStop): 'pending' | 'active' | 'completed' | 'visited' => {
  // Use computed_status from backend if available
  if (stop.computed_status) {
    if (stop.computed_status === 'visited') return 'completed'; // Treat visited as completed
    return stop.computed_status as 'pending' | 'active' | 'completed';
  }
  
  // Fallback to entry/exit count logic
  if (stop.entry_count && stop.entry_count > 0) {
    return 'completed'; // If vehicle entered, consider it completed
  }
  
  // Otherwise use database status
  return stop.status;
};

const TripPlanning: React.FC = () => {
  const { isCompanyStaff } = useAuthStore();
  const [trips, setTrips] = useState<Trip[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [stops, setStops] = useState<Stop[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [expandedTripId, setExpandedTripId] = useState<number | null>(null);
  const [showTripDetails, setShowTripDetails] = useState(true);
  // const [autoUpdateEnabled, setAutoUpdateEnabled] = useState(true);
  const [replayPositions, setReplayPositions] = useState<any[]>([]);
  const [isReplaying, setIsReplaying] = useState(false);
  const [replayIndex, setReplayIndex] = useState(0);
  const [checkingProgress, setCheckingProgress] = useState<number | null>(null);

  useEffect(() => {
    fetchData();
  }, []);

  // Replay animation effect
  useEffect(() => {
    if (!isReplaying || replayPositions.length === 0) return;

    const interval = setInterval(() => {
      setReplayIndex(prev => {
        if (prev >= replayPositions.length - 1) {
          setIsReplaying(false);
          return prev;
        }
        return prev + 1;
      });
    }, 100); // Move to next position every 100ms for smooth animation

    return () => clearInterval(interval);
  }, [isReplaying, replayPositions]);

  // Check trip progress when trips are loaded
  useEffect(() => {
    if (trips.length === 0) return;

    // Check each in-progress trip for status updates
    trips.forEach(async (trip) => {
      // Only check trips that are in progress but have no stop updates yet
      const hasNoUpdates = trip.stops.every(stop => 
        stop.status === 'pending' || (stop.status === 'active' && !stop.actual_time)
      );
      
      if (hasNoUpdates && trip.stops.length > 0) {
        try {
          const result = await tripApi.checkProgressFromHistory(trip.id);
          if (result.success && result.data.updates && result.data.updates.length > 0) {
            console.log(`Trip ${trip.job_number} progress updated from history:`, result.data);
            // Refresh trip data after checking history
            setTimeout(() => fetchData(), 1000);
          }
        } catch (error) {
          console.error(`Failed to check trip ${trip.job_number} progress from history:`, error);
        }
      }
    });
  }, [trips.length]); // Only run when number of trips changes

  // Auto-update trip progress based on GPS position every 10 seconds
  useEffect(() => {
    // if (!autoUpdateEnabled) return;

    const intervalId = setInterval(async () => {
      for (const trip of trips) {
        if (trip.vehicle_lat && trip.vehicle_lng && trip.vehicle_lat !== 0 && trip.vehicle_lng !== 0) {
          try {
            const result = await tripApi.autoUpdateProgress(trip.id, trip.vehicle_lat, trip.vehicle_lng);
            
            if (result.success && result.data.updated) {
              console.log(`Trip ${trip.job_number} auto-updated:`, result.data);
              // Refresh trip data to show updated status
              fetchData();
              break; // Only update one trip per interval to avoid race conditions
            }
          } catch (error) {
            console.error(`Failed to auto-update trip ${trip.job_number}:`, error);
          }
        }
      }
    }, 10000); // Check every 10 seconds

    return () => clearInterval(intervalId);
  }, [trips]);

  const fetchData = async () => {
    try {
      const [routesRes, stopsRes, vehiclesRes, driversRes, tripsRes] = await Promise.all([
        routeApi.getAll(),
        stopApi.getAll(),
        vehicleApi.getAll(),
        driverApi.getAll(),
        tripApi.getAll(),
      ]);

      if (routesRes.success && routesRes.data) setRoutes(routesRes.data);
      if (stopsRes.success && stopsRes.data) setStops(stopsRes.data);
      if (vehiclesRes.success && vehiclesRes.data) setVehicles(vehiclesRes.data);
      if (driversRes.success && driversRes.data) setDrivers(driversRes.data);

      // Map trips from API to component format
      if (tripsRes.success && tripsRes.data) {
        const mappedTrips = tripsRes.data.map((trip: any) => {
          // Get vehicle position (from Traccar or first stop)
          const vehiclePosition = trip.vehicle_position || trip.stops?.[0] || { latitude: 0, longitude: 0 };
          
          // Map stops to TripStop format
          const mappedStops: TripStop[] = trip.stops?.map((stop: any) => ({
            id: stop.trip_stop_id,
            name: stop.stop_name,
            type: stop.stop_type as any, // Use the actual stop type from database
            address: stop.address,
            latitude: parseFloat(stop.latitude),
            longitude: parseFloat(stop.longitude),
            sequence: stop.sequence_order,
            estimated_time: stop.estimated_arrival ? new Date(stop.estimated_arrival).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : '',
            status: stop.stop_status as any,
            actual_time: stop.actual_arrival ? new Date(stop.actual_arrival).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }) : undefined,
            // Event-based fields from GPS tracking
            first_entry: stop.first_entry,
            last_exit: stop.last_exit,
            entry_count: stop.entry_count || 0,
            exit_count: stop.exit_count || 0,
            computed_status: stop.computed_status,
            status_reason: stop.status_reason,
          })) || [];

          return {
            id: trip.id,
            job_number: trip.trip_number,
            vehicle_name: `${trip.vehicle_name} (${trip.vehicle_registration})`,
            driver_name: trip.driver_name,
            vehicle_lat: parseFloat(vehiclePosition.latitude),
            vehicle_lng: parseFloat(vehiclePosition.longitude),
            cargo: trip.cargo_description || 'No cargo description',
            status: trip.trip_status || 'pending',
            stops: mappedStops,
          };
        });
        
        setTrips(mappedTrips);
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    } finally {
      setLoading(false);
    }
  };

  const createVehicleIcon = () => {
    return L.divIcon({
      className: 'custom-vehicle-marker',
      html: `
        <div style="position: relative;">
          <div style="width: 40px; height: 40px; background: #059669; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
              <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
            </svg>
          </div>
        </div>
      `,
      iconSize: [40, 40],
      iconAnchor: [20, 20],
    });
  };

  const createStopIcon = (type: string, status: string) => {
    const bgColor = status === 'completed' ? '#059669' : status === 'active' ? '#3b82f6' : '#d1d5db';
    let emoji = '⭕';
    if (type === 'pickup') emoji = '📦';
    else if (type === 'delivery') emoji = '📍';
    else if (type === 'depot') emoji = '🏢';
    else if (type === 'loading') emoji = '⏳';
    
    return L.divIcon({
      className: 'custom-stop-marker',
      html: `
        <div style="width: 32px; height: 32px; background: ${bgColor}; border-radius: 50%; display: flex; align-items: center; justify-content: center; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.2);">
          <span style="font-size: 16px;">${emoji}</span>
        </div>
      `,
      iconSize: [32, 32],
      iconAnchor: [16, 16],
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const handleStartTrip = async (tripId: number) => {
    try {
      await tripApi.startTrip(tripId);
      fetchData(); // Refresh to show updated trip status
    } catch (error) {
      console.error('Failed to start trip:', error);
    }
  };

  const handleCheckProgress = async (tripId: number) => {
    setCheckingProgress(tripId);
    try {
      console.log('Checking trip progress from GPS history...');
      const result = await tripApi.checkProgressFromHistory(tripId);
      console.log('=== Progress Check Result ===');
      console.log('Success:', result.success);
      console.log('Message:', result.message);
      console.log('Data:', result.data);
      console.log('Updates made:', result.data?.updates?.length || 0);
      console.log('Positions analyzed:', result.data?.positions_analyzed || 0);
      console.log('Device ID:', result.data?.device_id);
      console.log('Time range:', result.data?.from, 'to', result.data?.to);
      console.log('============================');
      
      // Log debug info for each stop
      if (result.data?.debug_info) {
        console.log('=== STOP DETECTION DEBUG ===');
        result.data.debug_info.forEach((debug: any, index: number) => {
          console.log(`\nStop ${index + 1}: ${debug.stop_name}`);
          console.log('  Stop ID:', debug.stop_id);
          console.log('  Current Status:', debug.current_status);
          console.log('  Action:', debug.action);
          if (debug.existing_events) console.log('  Existing Events:', debug.existing_events);
          if (debug.events_found !== undefined) console.log('  Events Found:', debug.events_found);
          if (debug.events) {
            console.log('  Events:', debug.events);
          }
        });
        console.log('============================');
      }
      
      // Log stop statuses
      if (result.data?.stop_statuses) {
        console.log('=== STOP STATUSES (from events) ===');
        result.data.stop_statuses.forEach((status: any, index: number) => {
          console.log(`\nStop ${index + 1}: ${status.stop_name}`);
          console.log('  First Entry:', status.first_entry || 'None');
          console.log('  Last Exit:', status.last_exit || 'None');
          console.log('  Entry Count:', status.entry_count);
          console.log('  Exit Count:', status.exit_count);
        });
        console.log('============================');
      }
      
      if (result.success) {
        console.log('Stop updates:', result.data.updates);
        
        // Always refresh the data regardless of updates
        console.log('Refreshing trip data...');
        await fetchData();
        
        // Show summary after refresh
        if (result.data?.updates && result.data.updates.length > 0) {
          console.log(`✅ ${result.data.updates.length} stop(s) updated successfully`);
        } else if (result.data?.positions_analyzed === 0) {
          console.log('⚠️ No GPS positions found in Traccar for this time period');
        } else {
          console.log(`ℹ️ Analyzed ${result.data?.positions_analyzed || 0} positions but no new updates needed`);
        }
      }
    } catch (error) {
      console.error('Failed to check progress:', error);
      alert('Error checking progress. See console for details.');
    } finally {
      setCheckingProgress(null);
    }
  };

  const handleReplayTrip = async (tripId: number) => {
    try {
      console.log('Loading trip replay positions...');
      const result = await tripApi.getReplayPositions(tripId);
      
      if (result.success && result.data.positions) {
        console.log(`✅ Loaded ${result.data.total_positions} positions for replay from ${result.data.start_time} to ${result.data.end_time || 'now'}`);
        setReplayPositions(result.data.positions);
        setReplayIndex(0);
        setIsReplaying(true);
        // TODO: Implement map replay visualization
      } else {
        console.log('⚠️ No replay data available for this trip');
      }
    } catch (error) {
      console.error('❌ Failed to load replay:', error);
    }
  };

  const handleStopReplay = () => {
    setIsReplaying(false);
    setReplayPositions([]);
    setReplayIndex(0);
  };

  // const handleStopStatusUpdate = async (tripId: number, stopId: number, newStatus: 'active' | 'completed') => {
  //   try {
  //     // Call API to update stop status
  //     await tripApi.updateStopStatus(tripId, stopId, newStatus);
  //     
  //     // Update local state after successful API call
  //     setTrips(prevTrips => 
  //       prevTrips.map(trip => {
  //         if (trip.id !== tripId) return trip;
  //         
  //         return {
  //           ...trip,
  //           stops: trip.stops.map((stop, index) => {
  //             if (stop.id === stopId) {
  //               return {
  //                 ...stop,
  //                 status: newStatus,
  //                 actual_time: newStatus === 'completed' ? new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: false }) : stop.actual_time
  //               };
  //             }
  //             // Auto-activate next stop when previous is completed
  //             if (newStatus === 'completed' && index > 0 && trip.stops[index - 1].id === stopId) {
  //               return { ...stop, status: 'active' };
  //             }
  //             return stop;
  //           })
  //         };
  //       })
  //     );
  //   } catch (error) {
  //     console.error('Failed to update stop status:', error);
  //     // Optionally show error message to user
  //   }
  // };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-fleet-dark">Trip Planning & Tracking</h1>
          <p className="text-fleet-neutral">Track trip progress and delivery stops in real-time</p>
        </div>
        {isCompanyStaff() && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn-primary flex items-center"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create Trip
          </button>
        )}
      </div>

      {/* Active Trips List */}
      {trips.map((trip) => {
        const completedStops = trip.stops.filter(s => getStopStatus(s) === 'completed').length;
        const activeStop = trip.stops.find(s => getStopStatus(s) === 'active');
        const progressPercent = Math.round((completedStops / trip.stops.length) * 100);
        const isExpanded = expandedTripId === trip.id;

        return (
          <div key={trip.id} className="bg-white rounded-lg shadow-fleet overflow-hidden">
            {/* Collapsed View: Horizontal Timeline */}
            {!isExpanded && (
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-4">
                  <div className="bg-fleet-primary bg-opacity-10 p-3 rounded-lg">
                    <Truck className="h-6 w-6 text-fleet-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-fleet-dark">Trip #{trip.job_number}</h3>
                    <p className="text-sm text-fleet-neutral">
                      {trip.vehicle_name} • {trip.driver_name}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-4">
                  <div className="text-right">
                    <p className="text-2xl font-bold text-fleet-primary">{progressPercent}%</p>
                    <p className="text-xs text-fleet-neutral">Complete</p>
                  </div>
                  <button
                    onClick={() => setExpandedTripId(trip.id)}
                    className="px-4 py-2 bg-fleet-primary text-white rounded-lg hover:bg-fleet-secondary transition-colors"
                  >
                    View Details
                  </button>
                </div>
              </div>
              
              {/* Cargo Info */}
              <div className="mb-4 flex items-center space-x-2 text-sm">
                <Package className="h-4 w-4 text-fleet-neutral" />
                <span className="font-medium text-fleet-dark">{trip.cargo}</span>
              </div>

              {/* Horizontal Timeline */}
              <div className="mb-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-medium text-fleet-dark">Trip Progress</p>
                    {activeStop && (
                      <p className="text-sm text-blue-600 font-medium">Current: {activeStop.name}</p>
                    )}
                  </div>
                
                <div className="relative">
                  <div className="flex items-center justify-between">
                    {/* Only show main stages in horizontal view */}
                    {trip.stops.filter(s => ['pending', 'accepted', 'pickup', 'loading', 'en_route', 'delivery', 'depot'].includes(s.type)).map((stop, index, arr) => {
                      const stopStatus = getStopStatus(stop);
                      return (
                      <div key={stop.id} className="flex flex-col items-center" style={{ width: `${100 / arr.length}%` }}>
                        {/* Connector Line */}
                        {index < arr.length - 1 && (
                          <div 
                            className={`absolute h-1 ${
                              stopStatus === 'completed' ? 'bg-green-500' : 
                              stopStatus === 'active' ? 'bg-blue-500' : 'bg-gray-300'
                            }`}
                            style={{ 
                              left: `${(index * 100) / arr.length + (50 / arr.length)}%`,
                              width: `${100 / arr.length}%`,
                              top: '20px'
                            }}
                          />
                        )}
                        
                        {/* Status Circle */}
                        <div className="relative z-10 mb-2">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            stopStatus === 'completed' ? 'bg-green-500' :
                            stopStatus === 'active' ? 'bg-blue-500 ring-4 ring-blue-200' : 'bg-gray-300'
                          }`}>
                            {stopStatus === 'completed' ? (
                              <CheckCircle className="h-5 w-5 text-white" />
                            ) : stopStatus === 'active' ? (
                              <Circle className="h-5 w-5 text-white fill-current" />
                            ) : (
                              <Circle className="h-4 w-4 text-gray-500" />
                            )}
                          </div>
                        </div>
                        
                        {/* Label */}
                        <p className={`text-xs text-center ${
                          stopStatus === 'completed' || stopStatus === 'active' 
                            ? 'font-semibold text-fleet-dark' 
                            : 'text-gray-500'
                        }`}>
                          {stop.name}
                        </p>
                        
                        {/* Show first entry time if available */}
                        {stop.first_entry && (
                          <p className="text-xs text-green-600 mt-1">
                            {new Date(stop.first_entry).toLocaleTimeString()}
                          </p>
                        )}
                      </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
            )}

            {/* Expanded View: Vertical Timeline + Map */}
            {isExpanded && (
              <div className="border-t border-gray-200">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 max-h-[85vh]">
              {/* Left: Vertical Timeline */}
              <div className="p-6 border-r border-gray-200 overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="text-sm font-semibold text-fleet-dark flex items-center">
                    <Clock className="h-4 w-4 mr-2" />
                    Trip Progress
                  </h4>
                  <div className="flex items-center space-x-2">
                    {(trip.status === 'pending' || trip.status === 'accepted') && (
                      <button
                        onClick={() => handleStartTrip(trip.id)}
                        className="px-3 py-1.5 bg-fleet-primary text-white text-sm rounded-lg hover:bg-fleet-secondary transition-colors font-medium"
                      >
                        🚀 Start Trip
                      </button>
                    )}
                    {trip.status === 'in_progress' && (
                      <button
                        onClick={() => handleCheckProgress(trip.id)}
                        disabled={checkingProgress === trip.id}
                        className={`px-3 py-1.5 text-white text-sm rounded-lg transition-colors flex items-center space-x-2 ${
                          checkingProgress === trip.id 
                            ? 'bg-blue-400 cursor-not-allowed' 
                            : 'bg-blue-600 hover:bg-blue-700'
                        }`}
                        title="Check progress from GPS history"
                      >
                        {checkingProgress === trip.id ? (
                          <>
                            <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            <span>Analyzing...</span>
                          </>
                        ) : (
                          <span>Check Progress</span>
                        )}
                      </button>
                    )}
                    {trip.status === 'in_progress' && (
                      <button
                        onClick={() => handleReplayTrip(trip.id)}
                        className="px-3 py-1.5 bg-purple-600 text-white text-sm rounded-lg hover:bg-purple-700 transition-colors"
                        title="Replay trip from GPS history"
                      >
                        🎬 Replay Trip
                      </button>
                    )}
                    <button
                      onClick={() => {
                        setExpandedTripId(null);
                        setShowTripDetails(true);
                      }}
                      className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors"
                      title="Collapse"
                    >
                      <ChevronUp className="h-5 w-5 text-fleet-neutral" />
                    </button>
                  </div>
                </div>
                
                <div className="relative">
                  {trip.stops.map((stop, index) => {
                    const stopStatus = getStopStatus(stop);
                    return (
                    <div key={stop.id} className="flex items-start mb-3 last:mb-0">
                      {/* Timeline Line */}
                      {index < trip.stops.length - 1 && (
                        <div 
                          className={`absolute left-3 w-0.5 h-full ${
                            stopStatus === 'completed' ? 'bg-green-500' : 'bg-gray-300'
                          }`}
                          style={{ 
                            top: '28px',
                            height: 'calc(100% - 28px)'
                          }}
                        />
                      )}
                      
                      {/* Icon */}
                      <div className="relative z-10 mr-2">
                        <div 
                          className={`w-6 h-6 rounded-full flex items-center justify-center border-2 border-white shadow-sm ${
                            stopStatus === 'completed' 
                              ? 'bg-green-500' 
                              : stopStatus === 'active'
                              ? 'bg-blue-500 ring-4 ring-blue-100'
                              : 'bg-gray-300'
                          }`}
                        >
                          {stopStatus === 'completed' ? (
                            <CheckCircle className="h-3 w-3 text-white" />
                          ) : stopStatus === 'active' ? (
                            <Circle className="h-3 w-3 text-white fill-current" />
                          ) : (
                            <Circle className="h-2 w-2 text-gray-500" />
                          )}
                        </div>
                      </div>
                      
                      {/* Content */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between mb-0.5">
                          <h5 className={`text-xs font-semibold ${
                            stopStatus === 'completed' || stopStatus === 'active'
                              ? 'text-fleet-dark'
                              : 'text-gray-500'
                          }`}>
                            {stop.name}
                          </h5>
                          <span className={`text-[10px] font-medium ${
                            stopStatus === 'completed' || stopStatus === 'active'
                              ? 'text-fleet-primary'
                              : 'text-gray-400'
                          }`}>
                            {stop.actual_time || stop.estimated_time}
                          </span>
                        </div>
                        <p className="text-[10px] text-fleet-neutral line-clamp-1 mb-1">{stop.address}</p>
                        
                        {/* Show status based on events */}
                        {stopStatus === 'completed' && stop.first_entry && (
                          <div className="flex flex-col space-y-1">
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-green-100 text-green-800">
                              ✓ Completed
                            </span>
                            <span className="text-[10px] text-gray-600">
                              Arrived: {new Date(stop.first_entry).toLocaleTimeString()}
                            </span>
                            {stop.last_exit && (
                              <span className="text-[10px] text-gray-600">
                                Departed: {new Date(stop.last_exit).toLocaleTimeString()}
                              </span>
                            )}
                            {stop.entry_count && stop.entry_count > 1 && (
                              <span className="text-[10px] text-gray-500">
                                {stop.entry_count} visit{stop.entry_count > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        )}
                        
                        {stopStatus === 'active' && !stop.first_entry && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-blue-100 text-blue-800">
                            ⏳ In Progress
                          </span>
                        )}
                        
                        {stopStatus === 'pending' && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-600">
                            ⏱ Pending
                          </span>
                        )}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>

              {/* Right: Map */}
              <div className="relative h-full min-h-[400px]">
                {/* Trip Details Overlay */}
                <div className="absolute top-4 left-4 z-[1000] bg-white rounded-lg shadow-lg max-w-sm">
                  {showTripDetails ? (
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className="bg-fleet-primary bg-opacity-10 p-2 rounded-lg">
                            <Truck className="h-5 w-5 text-fleet-primary" />
                          </div>
                          <div>
                            <h3 className="text-base font-semibold text-fleet-dark">Trip #{trip.job_number}</h3>
                            <p className="text-xs text-fleet-neutral">{progressPercent}% Complete</p>
                          </div>
                        </div>
                        <button
                          onClick={() => setShowTripDetails(false)}
                          className="p-1 hover:bg-gray-100 rounded transition-colors"
                          title="Hide details"
                        >
                          <ChevronUp className="h-4 w-4 text-fleet-neutral" />
                        </button>
                      </div>
                      <div className="space-y-2 text-sm border-t pt-3">
                        <div className="flex items-center space-x-2">
                          <Truck className="h-4 w-4 text-fleet-neutral" />
                          <span className="text-fleet-neutral">Vehicle:</span>
                          <span className="font-medium text-fleet-dark">{trip.vehicle_name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <User className="h-4 w-4 text-fleet-neutral" />
                          <span className="text-fleet-neutral">Driver:</span>
                          <span className="font-medium text-fleet-dark">{trip.driver_name}</span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Box className="h-4 w-4 text-fleet-neutral" />
                          <span className="text-fleet-neutral">Cargo:</span>
                          <span className="font-medium text-fleet-dark">{trip.cargo}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowTripDetails(true)}
                      className="p-3 hover:bg-gray-50 rounded-lg transition-colors flex items-center space-x-2"
                      title="Show trip details"
                    >
                      <Truck className="h-5 w-5 text-fleet-primary" />
                      <span className="text-sm font-medium text-fleet-dark">Trip #{trip.job_number}</span>
                    </button>
                  )}
                </div>

                <MapContainer
                  center={[trip.vehicle_lat, trip.vehicle_lng]}
                  zoom={11}
                  style={{ height: '100%', width: '100%' }}
                  zoomControl={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  
                  {/* Vehicle Marker */}
                  <Marker
                    position={[trip.vehicle_lat, trip.vehicle_lng]}
                    icon={createVehicleIcon()}
                  >
                    <Popup>
                      <div className="text-sm">
                        <p className="font-semibold">{trip.vehicle_name}</p>
                        <p className="text-gray-600">{trip.driver_name}</p>
                        <p className="text-xs text-gray-500 mt-1">Current Location</p>
                      </div>
                    </Popup>
                  </Marker>

                  {/* Stop Markers */}
                  {trip.stops
                    .filter(stop => stop.latitude && stop.longitude && ['pickup', 'delivery', 'depot'].includes(stop.type))
                    .map((stop) => {
                      const stopStatus = getStopStatus(stop);
                      return (
                      <Marker
                        key={stop.id}
                        position={[stop.latitude, stop.longitude]}
                        icon={createStopIcon(stop.type, stopStatus)}
                      >
                        <Popup>
                          <div className="text-sm">
                            <p className="font-semibold">{stop.name}</p>
                            <p className="text-gray-600 text-xs">{stop.address}</p>
                            <p className="text-xs text-gray-500 mt-1">
                              {stopStatus === 'completed' ? '✓ Completed' : stopStatus === 'active' ? '⏳ In Progress' : '⏱ Scheduled'}
                            </p>
                            {stop.first_entry && (
                              <p className="text-xs text-green-600 mt-1">
                                Arrived: {new Date(stop.first_entry).toLocaleTimeString()}
                              </p>
                            )}
                          </div>
                        </Popup>
                      </Marker>
                      );
                    })}

                  {/* Route Line */}
                  <Polyline
                    positions={trip.stops
                      .filter(s => s.latitude && s.longitude && ['pickup', 'delivery', 'depot'].includes(s.type))
                      .map(s => [s.latitude, s.longitude] as [number, number])}
                    color="#3b82f6"
                    weight={3}
                    opacity={0.6}
                    dashArray="10, 10"
                  />

                  {/* Replay Route and Position */}
                  {isReplaying && replayPositions.length > 0 && (
                    <>
                      {/* Full replay route */}
                      <Polyline
                        positions={replayPositions.map(p => [p.latitude, p.longitude] as [number, number])}
                        color="#10b981"
                        weight={4}
                        opacity={0.8}
                      />
                      
                      {/* Animated vehicle position during replay */}
                      <Marker
                        position={[replayPositions[replayIndex].latitude, replayPositions[replayIndex].longitude]}
                        icon={createVehicleIcon()}
                      >
                        <Popup>
                          <div className="text-sm">
                            <p className="font-semibold">Trip Replay</p>
                            <p className="text-xs text-gray-600">Position {replayIndex + 1} of {replayPositions.length}</p>
                            <p className="text-xs text-gray-500">{new Date(replayPositions[replayIndex].timestamp).toLocaleString()}</p>
                            <p className="text-xs text-gray-500">Speed: {Math.round(replayPositions[replayIndex].speed * 1.852)} km/h</p>
                          </div>
                        </Popup>
                      </Marker>

                      {/* Replay controls overlay */}
                      <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-[1000] bg-white rounded-lg shadow-lg p-4">
                        <div className="flex items-center space-x-4">
                          <div className="text-sm">
                            <p className="font-semibold">Trip Replay</p>
                            <p className="text-xs text-gray-600">
                              {new Date(replayPositions[replayIndex].timestamp).toLocaleTimeString()} - 
                              Position {replayIndex + 1}/{replayPositions.length}
                            </p>
                          </div>
                          <button
                            onClick={handleStopReplay}
                            className="px-3 py-1.5 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 transition-colors"
                          >
                            Stop Replay
                          </button>
                        </div>
                        <div className="mt-2">
                          <div className="w-64 bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-green-600 h-2 rounded-full transition-all duration-100"
                              style={{ width: `${((replayIndex + 1) / replayPositions.length) * 100}%` }}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                </MapContainer>
              </div>
            </div>
            </div>
            )}
          </div>
        );
      })}

      {/* Create Trip Modal */}
      {showCreateModal && (
        <TripPlanningForm
          routes={routes}
          stops={stops}
          vehicles={vehicles}
          drivers={drivers}
          onClose={() => setShowCreateModal(false)}
          onSubmit={(tripData) => {
            console.log('Create trip:', tripData);
            setShowCreateModal(false);
          }}
        />
      )}
    </div>
  );
};

interface TripPlanningFormProps {
  routes: Route[];
  stops: Stop[];
  vehicles: Vehicle[];
  drivers: Driver[];
  onClose: () => void;
  onSubmit: (data: any) => void;
}

const TripPlanningForm: React.FC<TripPlanningFormProps> = ({
  routes,
  stops,
  vehicles,
  drivers,
  onClose,
  onSubmit,
}) => {
  const [selectedRoute, setSelectedRoute] = useState<number | null>(null);
  const [selectedStops, setSelectedStops] = useState<number[]>([]);
  const [formData, setFormData] = useState({
    vehicle_id: '',
    driver_id: '',
    cargo: '',
    scheduled_pickup: '',
    scheduled_dropoff: '',
  });

  const handleRouteSelect = (routeId: number) => {
    setSelectedRoute(routeId);
  };

  const toggleStop = (stopId: number) => {
    setSelectedStops(prev => 
      prev.includes(stopId) 
        ? prev.filter(id => id !== stopId)
        : [...prev, stopId]
    );
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      route_id: selectedRoute,
      stop_ids: selectedStops,
    });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <h3 className="text-xl font-bold text-fleet-dark mb-6">Create New Trip</h3>
          
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Vehicle & Driver */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Vehicle *</label>
                <select
                  value={formData.vehicle_id}
                  onChange={(e) => setFormData({ ...formData, vehicle_id: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary"
                >
                  <option value="">Select Vehicle</option>
                  {vehicles.map(v => (
                    <option key={v.id} value={v.id}>{v.name} ({v.registration})</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Driver *</label>
                <select
                  value={formData.driver_id}
                  onChange={(e) => setFormData({ ...formData, driver_id: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-fleet-primary"
                >
                  <option value="">Select Driver</option>
                  {drivers.map(d => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Route Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Route</label>
              <div className="grid grid-cols-1 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {routes.map(route => (
                  <label key={route.id} className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="radio"
                      name="route"
                      checked={selectedRoute === route.id}
                      onChange={() => handleRouteSelect(route.id)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-fleet-dark">{route.name}</p>
                      <p className="text-xs text-gray-500">{route.description}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Stops Selection */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Delivery Stops</label>
              <div className="grid grid-cols-2 gap-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
                {stops.map(stop => (
                  <label key={stop.id} className="flex items-center p-2 hover:bg-gray-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedStops.includes(stop.id)}
                      onChange={() => toggleStop(stop.id)}
                      className="mr-3"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-fleet-dark">{stop.name}</p>
                      <p className="text-xs text-gray-500">{stop.address}</p>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            {/* Cargo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Cargo Description *</label>
              <input
                type="text"
                value={formData.cargo}
                onChange={(e) => setFormData({ ...formData, cargo: e.target.value })}
                required
                placeholder="e.g., Electronics - 5 Pallets"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>

            {/* Schedule */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Pickup Time *</label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_pickup}
                  onChange={(e) => setFormData({ ...formData, scheduled_pickup: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Estimated Completion *</label>
                <input
                  type="datetime-local"
                  value={formData.scheduled_dropoff}
                  onChange={(e) => setFormData({ ...formData, scheduled_dropoff: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                />
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-4 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-6 py-2 bg-fleet-primary text-white rounded-lg hover:bg-fleet-secondary"
              >
                Create Trip
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default TripPlanning;
